// src/pages/HeathrowSurvey.jsx
import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import ReviewsBar from "../components/ReviewsBar";
import api from "../utils/api";
import "../styles/heathrowSurvey.css";

const HeathrowSurvey = () => {
  // --- Step control
  const [step, setStep] = useState(1);

  // --- Form state (matches your backend payload)
  const [terminal, setTerminal] = useState("");
  const [overallRating, setOverallRating] = useState(0);
  const [stressLevel, setStressLevel] = useState(0);

  const [issues, setIssues] = useState([]);
  const [positives, setPositives] = useState([]);

  const [transportUsed, setTransportUsed] = useState([]);
  const [taxiPaidRange, setTaxiPaidRange] = useState("");
  const [taxiFairPriceRange, setTaxiFairPriceRange] = useState("");
  const [considerNextTaxi, setConsiderNextTaxi] = useState("");
  const [expectedPrebookFareRange, setExpectedPrebookFareRange] = useState("");

  const [toolsFeedback, setToolsFeedback] = useState({
    arrival_assistant: null,
    parking_vs_taxi: null,
    train_vs_taxi: null,
    leave_for_heathrow: null,
    flight_checker: null,
    fare_compare: null,
    terminal_finder: null,
    airline_contacts: null
  });

  const [nationality, setNationality] = useState("");
  const [journeyArea, setJourneyArea] = useState("");
  const [email, setEmail] = useState("");
  const [source, setSource] = useState("ON_SITE");
  const [consent, setConsent] = useState(false);
  const [additionalComments, setAdditionalComments] = useState("");

  // --- Submit state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const resetForm = () => {
    setStep(1);
    setTerminal("");
    setOverallRating(0);
    setStressLevel(0);
    setIssues([]);
    setPositives([]);
    setTransportUsed([]);
    setTaxiPaidRange("");
    setTaxiFairPriceRange("");
    setConsiderNextTaxi("");
    setExpectedPrebookFareRange("");
    setToolsFeedback({
      arrival_assistant: null,
      parking_vs_taxi: null,
      train_vs_taxi: null,
      leave_for_heathrow: null,
      flight_checker: null,
      fare_compare: null,
      terminal_finder: null,
      airline_contacts: null
    });
    setJourneyArea("");
    setNationality("");
    setEmail("");
    setSource("ON_SITE");
    setConsent(false);
    setAdditionalComments("");
  };

  // --- Config lists
  const terminalOptions = ["T2", "T3", "T4", "T5", "OTHER"];

  const issueOptions = [
    { code: "SECURITY_QUEUE", label: "Security queue" },
    { code: "IMMIGRATION_QUEUE", label: "Immigration / passport queue" },
    { code: "BAGGAGE_DELAY", label: "Baggage delay" },
    { code: "DROP_OFF_CHARGES", label: "Drop-off charges" },
    { code: "PARKING_CHARGES", label: "Parking charges" },
    { code: "SIGNAGE_CONFUSION", label: "Signage / finding your way" },
    { code: "CLEANLINESS", label: "Facilities / cleanliness" },
    { code: "STAFF_ISSUES", label: "Staff issue" },
    { code: "TRANSPORT_TAXI_QUEUE", label: "Taxi / transport queues" },
    { code: "OTHER", label: "Other" }
  ];

  const positiveOptions = [
    { code: "FRIENDLY_STAFF", label: "Friendly staff" },
    { code: "FAST_SECURITY", label: "Security was smooth" },
    { code: "FAST_IMMIGRATION", label: "Immigration was smooth" },
    { code: "GOOD_SIGNAGE", label: "Good signage / easy navigation" },
    { code: "CLEAN_TERMINAL", label: "Clean terminal" },
    { code: "GOOD_SHOPS_FOOD", label: "Shops / food options" },
    { code: "TRANSPORT_EASY", label: "Transport was easy" },
    { code: "OTHER", label: "Other" }
  ];

  const transportOptions = [
    { code: "PRIVATE_TAXI", label: "Pre-booked taxi / private hire" },
    { code: "RIDE_HAIL", label: "Ride-hailing app (Uber/Bolt etc.)" },
    { code: "LONDON_TAXI", label: "London taxi / taxi rank" },
    { code: "PARKING", label: "Car + parking" },
    { code: "DROP_OFF_PICKUP", label: "Family/friends pick-up or drop-off" },
    { code: "TRAIN", label: "Train / Tube" },
    { code: "BUS_COACH", label: "Bus / coach" },
    { code: "OTHER", label: "Other" }
  ];

  const priceRanges = [
    "0-30",
    "30-50",
    "50-80",
    "80-120",
    "120-200",
    "200+"
  ];
  const fareRanges = ["10-30", "30-50", "50-80", "80-120", "120-200"];

  const nationalityOptions = [
    "UK",
    "EU/EEA",
    "USA",
    "Canada",
    "India",
    "Pakistan",
    "UAE",
    "Saudi Arabia",
    "China",
    "Australia",
    "Other"
  ];

  const journeyAreaOptions = [
    { code: "CENTRAL_LONDON", label: "Central London (Zone 1–2)" },
    { code: "WEST_LONDON", label: "West London (near Heathrow)" },
    { code: "NORTH_LONDON", label: "North London" },
    { code: "EAST_LONDON", label: "East London" },
    { code: "SOUTH_LONDON", label: "South London" },
    { code: "SLOUGH_WINDSOR_MAIDENHEAD", label: "Slough / Windsor / Maidenhead" },
    { code: "BERKS_BUCKS_OTHER", label: "Berkshire / Buckinghamshire (other)" },
    { code: "OUTSIDE_LONDON_UK", label: "Outside London (UK)" },
    { code: "NOT_SURE", label: "Not sure" }
  ];

const usedTaxiToday =
    transportUsed.includes("PRIVATE_TAXI") ||
    transportUsed.includes("RIDE_HAIL") ||
    transportUsed.includes("LONDON_TAXI");
  const toolRows = useMemo(
    () => [
      {
        key: "arrival_assistant",
        title: "Arrival Assistant",
        link: "/arrival-assistant",
        label: "Would an Arrival Assistant (queues + step-by-step arrival guide) reduce your stress?"
      },
      {
        key: "parking_vs_taxi",
        title: "Parking vs Taxi",
        link: "/heathrow-parking-vs-taxi",
        label: "Would a Parking vs Taxi comparison tool help you decide?"
      },
      {
        key: "train_vs_taxi",
        title: "Train vs Taxi",
        link: "/train-vs-taxi",
        label: "Would a Train vs Taxi tool help you choose the best option?"
      },
      {
        key: "leave_for_heathrow",
        title: "Leave For Heathrow",
        link: "/leave-for-heathrow",
        label: "Would a ‘When should I leave?’ tool reduce your travel stress?"
      },
      {
        key: "flight_checker",
        title: "Flight Checker",
        link: "/flight-checker",
        label: "Would a Flight Checker help you plan pickup timing?"
      },
      {
        key: "fare_compare",
        title: "Fare Compare",
        link: "/heathrow-fare-compare",
        label: "Would a fare comparison tool help you trust the best value option?"
      },
      {
        key: "terminal_finder",
        title: "Terminal Finder",
        link: "/terminalFinder",
        label: "Would a terminal finder (airline → terminal) help you?"
      },
      {
        key: "airline_contacts",
        title: "Airline Contacts",
        link: "/airlineContacts",
        label: "Would quick airline contact info help during disruption?"
      }
    ],
    []
  );

  const toggleArrayValue = (arr, value) =>
    arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value];

  const setToolYesNo = (key, value) => {
    setToolsFeedback(prev => ({ ...prev, [key]: value }));
  };

  const usedTaxiLike =
    transportUsed.includes("PRIVATE_TAXI") ||
    transportUsed.includes("RIDE_HAIL") ||
    transportUsed.includes("LONDON_TAXI");

  const validateStep = () => {
    if (step === 1) {
      if (!terminal) return "Please select a terminal.";
      if (!overallRating) return "Please select an overall rating.";
      if (!stressLevel) return "Please select a stress level.";
    }
    if (step === 2) {
      if (issues.length === 0 && positives.length === 0) {
        return "Please select at least one issue or one positive.";
      }
    }
    if (step === 3) {
      if (transportUsed.length === 0) return "Please select how you travelled.";
      // If any taxi-like option selected, encourage ranges (not mandatory)
      const usedTaxi =
        transportUsed.includes("PRIVATE_TAXI") ||
        transportUsed.includes("RIDE_HAIL") ||
        transportUsed.includes("LONDON_TAXI");
      if (usedTaxi && !taxiPaidRange) return "Please choose the taxi price range you paid (or expected).";
      if (usedTaxi && !taxiFairPriceRange) return "Please choose the fare you feel is fair for your journey.";
    }
    if (step === 4) {
      if (!nationality) return "Please select your nationality.";
      if (!consent) return "Consent is required to submit this survey.";
      // email optional
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "Please enter a valid email (or leave it blank).";
    }
    return "";
  };

  const nextStep = () => {
    const v = validateStep();
    if (v) return setError(v);
    setError("");
    setStep(s => Math.min(4, s + 1));
    scrollToTop();
  };

  const prevStep = () => {
    setError("");
    setStep(s => Math.max(1, s - 1));
    scrollToTop();
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError("");
    setSuccess(null);

    const v = validateStep();
    if (v) return setError(v);

    const payload = {
      terminal,
      overall_rating: Number(overallRating),
      stress_level: Number(stressLevel),
      issues,
      positives,
      transport_used: transportUsed,
      taxi_paid_range: taxiPaidRange || null,
      taxi_fair_price_range: taxiFairPriceRange || null,
      consider_prebooked_taxi_next_time: considerNextTaxi || null,
      expected_prebooked_taxi_fare_range: expectedPrebookFareRange || null,
      tools_feedback: toolsFeedback,
      journey_area: journeyArea || null,
      nationality,
      email: email.trim() || null,
      source,
      consent: true,
      additional_comments: additionalComments?.trim() || null
    };

    try {
      setLoading(true);
      const res = await api.post("/api/surveys/heathrow", payload);
      setSuccess(res.data);
      resetForm();
      scrollToTop();
    } catch (err) {
      console.error("❌ Survey submit error:", err);
      const msg =
        err?.response?.data?.details ||
        err?.response?.data?.error ||
        "We could not submit your survey. Please try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const progress = Math.round((step / 4) * 100);

  if (success) {
    return (
      <div className="tool-page">
        <Helmet>
          <title>Heathrow Traveller Survey (Submitted) | CheapCabGrab</title>
        </Helmet>
        <main className="tool-container">
          <article className="tool-card survey-success-card">
            <h1>✅ Thanks — survey submitted</h1>
            <p>
              Reference: <strong>{success.survey_id}</strong>
              <br />
              Time:{" "}
              <strong>
                {success.created_at
                  ? new Date(success.created_at).toLocaleString("en-GB")
                  : "Just now"}
              </strong>
            </p>
            <p style={{ marginTop: 10, fontSize: 14, color: "#555" }}>
              If you entered your email, your 10% discount code will arrive shortly.
            </p>
            <div className="survey-success-actions">
              <Link className="primary-btn" to="/">
                Get an instant fare quote
              </Link>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => {
                  setSuccess(null);
                  scrollToTop();
                }}
              >
                Submit another survey
              </button>
              <Link className="secondary-btn" to="/arrival-assistant">
                Back to tools
              </Link>
            </div>
          </article>
        </main>
      </div>
    );
  }

  return (
    <div className="tool-page">
      <Helmet>
        <title>Heathrow Traveller Survey (2 Minutes) | CheapCabGrab</title>
        <meta
          name="description"
          content="Take a 2-minute Heathrow Traveller Survey. Help improve airport journeys and transport decisions using real passenger feedback. Optional 10% discount."
        />
        <meta
          name="keywords"
          content="Heathrow survey, Heathrow passenger experience, Heathrow queues survey, Heathrow taxi survey, Heathrow parking charges, airport passenger feedback"
        />
        <link rel="canonical" href="https://www.cheapcabgrab.com/heathrow-survey" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Heathrow Traveller Survey (2 Minutes)" />
        <meta
          property="og:description"
          content="Independent Heathrow passenger survey. Share your experience and help build better travel tools."
        />
        <meta property="og:url" content="https://www.cheapcabgrab.com/heathrow-survey" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Heathrow Traveller Survey",
            url: "https://www.cheapcabgrab.com/heathrow-survey",
            description:
              "Independent Heathrow passenger survey collecting feedback on queues, charges and transport decisions. Data is used to publish trends and improve travel tools.",
            about: [
              { "@type": "Place", name: "Heathrow Airport" },
              { "@type": "Service", name: "Airport Transfers", url: "https://www.cheapcabgrab.com/" }
            ]
          })}
        </script>
      </Helmet>

      <main className="tool-container">
        <header className="tool-header">
          <h1>📋 The Heathrow Traveller Experience Survey</h1>
          <p className="tool-intro">
            A quick, independent survey based on real passenger experiences at Heathrow.
            <br />
            Takes <strong>2–3 minutes</strong>. Optional email = <strong>10% discount code</strong>.
          </p>
        </header>

        <section className="tool-grid">
          {/* FORM */}
          <article className="tool-card">
            <div className="survey-progress">
              <div className="survey-progress-top">
                <strong>Step {step} of 4</strong>
                <span>{progress}%</span>
              </div>
              <div className="survey-progress-bar">
                <div className="survey-progress-fill" style={{ width: `${progress}%` }} />
              </div>
            </div>

            <form className="tool-form" onSubmit={handleSubmit}>
              {/* STEP 1 */}
              {step === 1 && (
                <>
                  <h2>✈️ Your Heathrow Experience</h2>

                  <div className="tool-form-row">
                    <label>Which terminal did you use today?</label>
                    <select value={terminal} onChange={e => setTerminal(e.target.value)}>
                      <option value="">Select terminal…</option>
                      {terminalOptions.map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>

                  <div className="survey-two-col">
                    <div className="tool-form-row">
                      <label>Overall rating (1 = poor, 5 = excellent)</label>
                      <div className="survey-rating">
                        {[1,2,3,4,5].map(n => (
                          <button
                            type="button"
                            key={n}
                            className={`survey-chip ${overallRating === n ? "active" : ""}`}
                            onClick={() => setOverallRating(n)}
                          >
                            {n}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="tool-form-row">
                      <label>Stress level today (1 = calm, 5 = stressed)</label>
                      <div className="survey-rating">
                        {[1,2,3,4,5].map(n => (
                          <button
                            type="button"
                            key={n}
                            className={`survey-chip ${stressLevel === n ? "active" : ""}`}
                            onClick={() => setStressLevel(n)}
                          >
                            {n}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <p className="survey-help">
                    Tip: If you didn’t face issues, choose a high rating and low stress — that’s still valuable.
                  </p>
                </>
              )}

              {/* STEP 2 */}
              {step === 2 && (
                <>
                  <h2>⚠️ What stood out today?</h2>

                  <div className="survey-block">
                    <h3>Worst issues (choose up to 3)</h3>
                    <div className="survey-options">
                      {issueOptions.map(opt => (
                        <label key={opt.code} className="survey-check">
                          <input
                            type="checkbox"
                            checked={issues.includes(opt.code)}
                            onChange={() => {
                              const next = toggleArrayValue(issues, opt.code);
                              // cap at 3
                              if (next.length <= 3) setIssues(next);
                            }}
                          />
                          <span>{opt.label}</span>
                        </label>
                      ))}
                    </div>
                    <div className="survey-note">Selected: {issues.length}/3</div>
                  </div>

                  <div className="survey-block" style={{ marginTop: 14 }}>
                    <h3>Positives (choose any)</h3>
                    <div className="survey-options">
                      {positiveOptions.map(opt => (
                        <label key={opt.code} className="survey-check">
                          <input
                            type="checkbox"
                            checked={positives.includes(opt.code)}
                            onChange={() => setPositives(toggleArrayValue(positives, opt.code))}
                          />
                          <span>{opt.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* STEP 3 */}
              {step === 3 && (
                <>
                  <h2>🚕 Your Transport Choices</h2>

                  <div className="survey-block">
                    <h3>How did you travel today?</h3>
                    <div className="survey-options">
                      {transportOptions.map(opt => (
                        <label key={opt.code} className="survey-check">
                          <input
                            type="checkbox"
                            checked={transportUsed.includes(opt.code)}
                            onChange={() => setTransportUsed(toggleArrayValue(transportUsed, opt.code))}
                          />
                          <span>{opt.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {usedTaxiLike && (
                    <div className="survey-two-col" style={{ marginTop: 14 }}>
                      <div className="tool-form-row">
                        <label>What taxi fare did you pay (or expect to pay)?</label>
                        <select value={taxiPaidRange} onChange={e => setTaxiPaidRange(e.target.value)}>
                          <option value="">Select range…</option>
                          {fareRanges.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </div>

                      <div className="tool-form-row">
                        <label>What fare feels fair for your journey?</label>
                        <select value={taxiFairPriceRange} onChange={e => setTaxiFairPriceRange(e.target.value)}>
                          <option value="">Select range…</option>
                          {fareRanges.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </div>
                    </div>
                  )}

                  {!usedTaxiLike && (
                    <>
                      <div className="tool-form-row" style={{ marginTop: 14 }}>
                        <label>Would you consider a pre-booked taxi next time?</label>
                        <select
                          value={considerNextTaxi}
                          onChange={e => setConsiderNextTaxi(e.target.value)}
                        >
                          <option value="">Select…</option>
                          <option value="YES">Yes</option>
                          <option value="MAYBE">Maybe</option>
                          <option value="NO">No</option>
                        </select>
                      </div>

                      <div className="survey-block" style={{ marginTop: 14 }}>
                        <h3>If you were to pre-book a taxi for this trip, what price would you expect?</h3>
                        <div className="tool-form-row" style={{ marginTop: 8 }}>
                          <select
                            value={expectedPrebookFareRange}
                            onChange={e => setExpectedPrebookFareRange(e.target.value)}
                          >
                            <option value="">Select range…</option>
                            {fareRanges.map(r => (
                              <option key={r} value={r}>{r}</option>
                            ))}
                          </select>
                          <p style={{ fontSize: 12, color: "#666", marginTop: 6 }}>
                            This helps us compare taxi vs train/parking fairly for different areas.
                          </p>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}

              {/* STEP 4 */}
              {step === 4 && (
                <>
                  <h2>🛠️ Quick tool feedback + optional discount</h2>

                  <div className="survey-block">
                    <h3>Would these tools help you?</h3>
                    <div className="survey-tools">
                      {toolRows.map(t => (
                        <div key={t.key} className="survey-tool-row">
                          <div className="survey-tool-left">
                            <strong>
                              <Link to={t.link}>{t.title}</Link>
                            </strong>
                            <div className="survey-tool-label">{t.label}</div>
                          </div>

                          <div className="survey-tool-right">
                            <button
                              type="button"
                              className={`survey-chip small ${toolsFeedback[t.key] === true ? "active" : ""}`}
                              onClick={() => setToolYesNo(t.key, true)}
                            >
                              Yes
                            </button>
                            <button
                              type="button"
                              className={`survey-chip small ${toolsFeedback[t.key] === false ? "active" : ""}`}
                              onClick={() => setToolYesNo(t.key, false)}
                            >
                              No
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="survey-two-col" style={{ marginTop: 14 }}>
                    <div className="tool-form-row">
                      <label>Your nationality</label>
                      <select value={nationality} onChange={e => setNationality(e.target.value)}>
                        <option value="">Select…</option>
                        {nationalityOptions.map(n => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                    </div>

                    <div className="tool-form-row">
                      <label>Email (optional — send 10% discount code)</label>
                      <input
                        type="email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        autoComplete="email"
                      />
                    </div>
                  </div>

                  <div className="survey-block" style={{ marginTop: 14 }}>
                    <h3>Which area best matches your journey?</h3>
                    <div className="tool-form-row" style={{ marginTop: 8 }}>
                      <select value={journeyArea} onChange={e => setJourneyArea(e.target.value)}>
                        <option value="">Select area…</option>
                        {journeyAreaOptions.map(o => (
                          <option key={o.code} value={o.code}>{o.label}</option>
                        ))}
                      </select>
                      <p style={{ fontSize: 12, color: "#666", marginTop: 6 }}>
                        Helps us group survey trends by location (no exact address needed).
                      </p>
                    </div>
                  </div>

                  <div className="tool-form-row" style={{ marginTop: 10 }}>
                    <label>Survey source</label>
                    <select value={source} onChange={e => setSource(e.target.value)}>
                      <option value="ON_SITE">On-site (at Heathrow)</option>
                      <option value="ONLINE">Online (later / from home)</option>
                      <option value="SOCIAL">Social link</option>
                    </select>
                  </div>

                  <label className="survey-consent">
                    <input
                      type="checkbox"
                      checked={consent}
                      onChange={e => setConsent(e.target.checked)}
                    />
                    <span>
                      I consent to submitting this survey. I understand data may be used in anonymous trend reporting.
                    </span>
                  </label>

                  <p className="survey-help">
                    You can view our <Link to="/privacy">Privacy Policy</Link>.
                  </p>

                  <div className="survey-block" style={{ marginTop: 12 }}>
                    <h3>Anything else you’d like to add? (optional)</h3>
                    <div className="tool-form-row" style={{ marginTop: 8 }}>
                      <textarea
                        value={additionalComments}
                        onChange={e => setAdditionalComments(e.target.value)}
                        placeholder="Any feedback about queues, charges, taxis, or anything we missed…"
                        rows={4}
                        style={{ resize: "vertical" }}
                        maxLength={500}
                      />
                      <p style={{ fontSize: 12, color: "#666", marginTop: 6 }}>
                        Max 500 characters.
                      </p>
                    </div>
                  </div>
                </>
              )}

              {error && <div className="tool-error" style={{ marginTop: 10 }}>{error}</div>}

              {/* Buttons */}
              <div className="survey-actions">
                <button
                  type="button"
                  className="secondary-btn"
                  onClick={prevStep}
                  disabled={step === 1 || loading}
                >
                  Back
                </button>

                {step < 4 ? (
                  <button
                    type="button"
                    className="primary-btn"
                    onClick={nextStep}
                    disabled={loading}
                  >
                    Next
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="primary-btn"
                    disabled={loading}
                  >
                    {loading ? "Submitting..." : "Submit Survey"}
                  </button>
                )}
              </div>
            </form>
          </article>

          {/* RIGHT / BELOW: Success + Links */}
          <article className="tool-card tool-results-card">
            <h2>What happens next?</h2>

            {!success && (
              <>
                <p style={{ fontSize: 14 }}>
                  We use independent passenger feedback to:
                </p>
                <ul style={{ marginTop: 8, fontSize: 14 }}>
                  <li>Publish monthly Heathrow experience trends</li>
                  <li>Improve arrival guidance and decision tools</li>
                  <li>Reduce passenger stress and confusion</li>
                </ul>

                <div className="survey-mini-links">
                  <strong>Useful tools:</strong>
                  <ul>
                    <li><Link to="/arrival-assistant">Arrival Assistant</Link></li>
                    <li><Link to="/flight-checker">Flight Checker</Link></li>
                    <li><Link to="/train-vs-taxi">Train vs Taxi</Link></li>
                    <li><Link to="/heathrow-parking-vs-taxi">Parking vs Taxi</Link></li>
                    <li><Link to="/heathrow-fare-compare">Fare Compare</Link></li>
                  </ul>
                </div>

                <div className="survey-block" style={{ textAlign: "center", marginTop: 12 }}>
                  <h3 style={{ marginBottom: 6 }}>Scan to open this survey</h3>
		<img
  src={`${api.defaults.baseURL}/api/qr?path=/heathrow-survey&size=600`}
  alt="Heathrow survey QR code"
  style={{ width: 220, maxWidth: "70%", borderRadius: 12 }}
/>

                  <div style={{ fontSize: 12, color: "#666", marginTop: 8 }}>
                    cheapcabgrab.com/heathrow-survey
                  </div>
                </div>
              </>
            )}

            {success && (
              <div className="survey-success">
                <h3>✅ Thank you — survey submitted</h3>
                <p style={{ fontSize: 14 }}>
                  Reference: <strong>{success.survey_id}</strong>
                  <br />
                  Submitted at:{" "}
                  <strong>
                    {new Date(success.created_at).toLocaleString("en-GB")}
                  </strong>
                </p>

                <p style={{ fontSize: 14, marginTop: 10 }}>
                  If you added an email, you’ll receive your discount code shortly.
                </p>

                <div className="survey-mini-links">
                  <strong>Plan your journey:</strong>
                  <ul>
                    <li><Link to="/heathrow-taxi">Heathrow Taxi Transfers</Link></li>
                    <li><Link to="/arrival-assistant">Arrival Assistant</Link></li>
                    <li><Link to="/flight-checker">Flight Checker</Link></li>
                    <li><Link to="/travelnews">UK Travel News</Link></li>
                  </ul>
                </div>

                <div className="survey-block" style={{ textAlign: "center", marginTop: 12 }}>
                  <h3 style={{ marginBottom: 6 }}>Scan to open this survey</h3>

<img
  src={`${api.defaults.baseURL}/api/qr?path=/heathrow-survey&size=600`}
  alt="Heathrow survey QR code"
  style={{ width: 220, maxWidth: "70%", borderRadius: 12 }}
/>



                  <div style={{ fontSize: 12, color: "#666", marginTop: 8 }}>
                    cheapcabgrab.com/heathrow-survey
                  </div>
                </div>
              </div>
            )}
          </article>
        </section>

        {/* Reviews at bottom (consistent) */}
        <section className="tool-section" style={{ marginTop: 32 }}>
          <h2>Customer Reviews</h2>
          <ReviewsBar />
        </section>
      </main>
    </div>
  );
};

export default HeathrowSurvey;
